import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Stack } from '@mui/material';
import { CheckCircle, Cancel, OpenInNew, Delete, Undo } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import client from 'utils/api';
import dayjs from 'dayjs';

const MAX_FILE_NAME_LENGTH = 25

const truncateFileName = name =>
  name && name.length > MAX_FILE_NAME_LENGTH ? `${name.slice(0, MAX_FILE_NAME_LENGTH)}…` : name

export default function Documents({
  currentCategoryInfo,
  currentCategoryDocs,
  onApprove,
  onReject,
  onUndo,
  onDeleteFile,
}) {
  const { t } = useTranslation();


  const download = async (event, file) => {
    event.preventDefault()
    try {
      const { data: url } = await client.get(`/api/file/${file?.file?.uuid}/url`)
      let a = window.document.createElement('a')
      a.href = url
      a.download = file?.file?.name
      a.target = '_blank'
      a.click()
    } catch (err) {
      console.log('---error--->', err.message)
    }
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight="600">
          {currentCategoryInfo.name}
        </Typography>
        {currentCategoryInfo.documents && currentCategoryInfo.documents[0] && (
          <Box>
            <Tooltip title={t("Undo Approval / Rejection")}>
              <IconButton
                size="small"
                color="info"
                onClick={() => onUndo(currentCategoryInfo?.documents[0])}
                disabled={currentCategoryInfo.documents && !(currentCategoryInfo.documents[0]?.status === 'approved' || currentCategoryInfo.documents[0]?.status === 'rejected')}
              >
                <Undo fontSize="medium" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("Approve")}>
              <IconButton
                size="small"
                color="success"
                onClick={() => onApprove(currentCategoryInfo.documents[0])}
                disabled={currentCategoryInfo.documents && currentCategoryInfo.documents[0]?.files.length === 0 || (currentCategoryInfo.documents[0]?.status === 'approved' || currentCategoryInfo.documents[0]?.status === 'rejected')}
              >
                <CheckCircle fontSize="medium" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("Reject")}>
              <IconButton
                size="small"
                color="error"
                onClick={() => onReject(currentCategoryInfo.documents[0])}
                disabled={currentCategoryInfo.documents && currentCategoryInfo.documents[0]?.files.length === 0 || (currentCategoryInfo.documents[0]?.status === 'approved' || currentCategoryInfo.documents[0]?.status === 'rejected')}
              >
                <Cancel fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>{t("File Name")}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentCategoryDocs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{t("No documents in this category")}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              currentCategoryDocs.map((doc) => (
                doc.files.length === 0 ? (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <Typography color="text.secondary" fontStyle="italic">
                        {t("No files uploaded")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">—</TableCell>
                  </TableRow>
                ) : (
                  doc.files.map((file, fileIdx) => {
                    const fileName = file?.file?.name || file?.name || `${t('File')} ${fileIdx + 1}`

                    return (
                    <TableRow key={`${doc.id}-${fileIdx}`} hover>
                      <TableCell>
                        <Tooltip
                          title={
                            <>
                              <Box>{fileName}</Box>
                              <Box>{`${t('File Uploaded On')}: ${dayjs(file?.createdAt).format('MM/DD/YYYY')}`}</Box>
                            </>
                          }
                          placement="top-start"
                        >
                          <Typography variant="body2" sx={{ cursor: 'default', width: 'fit-content' }}>
                            {truncateFileName(fileName)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title={t("Open in New Tab")}>
                            <IconButton size="small" onClick={(event) => download(event, file)}>
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("Delete")}>
                            <IconButton size="small" onClick={() => onDeleteFile(file)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    )
                  })
                )
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
